import React, { useEffect, useMemo } from 'react';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import { injectIntl, IntlShape } from 'react-intl';
import { ScheduledEmailFormVM } from './scheduled-email-form.viewmodel';
import { CommonPopupBodyFooterLayout } from '../../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../../global/sub-window/common/common-close-sub-window-buttons';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { DefaultDatePicker } from '../../../../../../global/default-datepicker/default-datepicker';
import { TimeInput } from '../../../../../../global/time-input/time-input';
import { RichTextEditor } from '../../../../../../global/rich-text-editor';
import { getFormattedTimezoneOffsetByTimezone, utcToZonedTime } from '../../../../../../../infrastructure/utils/date-fns-overrides.utils';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { UploadImageS3Presentation } from '../../../../shared/upload-image/upload-image-to-s3.presentation';
import {
    ADD_TEXT,
    APPLY_AND_CLOSE_TEXT,
    APPLY_TEXT,
    CANCEL_TEXT,
    DATE_TEXT,
    LOGO_IMAGE_TEXT
} from '../../../../../../../constants/common-labels.constants';
import {
    EMAIL_BODY_TEXT,
    EMAIL_IS_BEING_SAVED_TEXT,
    EMAIL_NAME_INTERNAL_TEXT,
    EMAIL_SUBJECT_TEXT,
    MEETING_TIME_TEXT,
    NOTIFICATION_ENABLED_INFO_MESSAGE_TEXT,
    SCHEDULED_MEETING_NOTIFICATION_TEXT
} from './scheduled-email-form.texts';
import { ScheduledEmailFormMeetingSection } from './_scheduled-email-form-meeting-section';

import './scheduled-email-form.scss';
import { FormFieldInput } from '../../../../../../global/form/form-field-input/form-field-input';
import Text from '../../../../../../global/text/text';
import { EnablementStatus, ONE_MINUTE } from '../../../../../../../constants/common.constants';
import { ScheduledEmailsFormHeader } from '../scheduled-emails-form-header/scheduled-emails-form-header';
import { EMAIL_LIST_TYPE } from '../../../../../../../constants/email-variables.constants';
import { EmailVariablesSection } from '../../../../../../global/email-variables/email-variables';

interface IProps {
    vm: ScheduledEmailFormVM;
    intl: IntlShape;
}

const ONE_HOUR = ONE_MINUTE * 60;

const ScheduledEmailFormPresentation = injectIntl(
    mobxReact.observer((props: IProps) => {
        const { vm, intl } = props;
        const { event, formData } = vm;
        const shouldShowMeetingInfo = !!(vm.meetingData && vm.meetingData.meetingId);
        const currentTime = utcToZonedTime(new Date(), vm.event.timeZone).getTime();
        const timeDifferenceHours = (vm.formData.dateTime.getTime() - currentTime) / ONE_HOUR;
        const isNotificationDisabled = timeDifferenceHours <= 2;

        const formattedTimezoneOffset = useMemo(() => {
            return getFormattedTimezoneOffsetByTimezone(event.timeZone);
        }, [event.timeZone, getFormattedTimezoneOffsetByTimezone]);

        useEffect(() => {
            if (isNotificationDisabled) vm.formData.setNotifyDeliveryTeam(false);
        }, [vm.formData.dateTime, vm.formData.date]);

        return (
            <CommonPopupBodyFooterLayout
                className='hidden-scroll'
                body={
                    <>
                    <ScheduledEmailsFormHeader
                        handleSendPreview={vm.handleSendPreview}
                        isEdit={vm.isEdit}
                        intl={intl}
                    />
                    <div className='scheduled-email-form emails-template-form'>
                        <div className='oe-form'>
                            {shouldShowMeetingInfo && <ScheduledEmailFormMeetingSection vm={vm} />}
                            <div>
                                <FormFieldInput.Toggle
                                    isSelected={vm.formData.getNotifyDeliveryTeam()}
                                    toggle={() => vm.formData.setNotifyDeliveryTeam(!vm.formData.getNotifyDeliveryTeam())}
                                    isDisabled={isNotificationDisabled}
                                    text={<Text
                                        {...SCHEDULED_MEETING_NOTIFICATION_TEXT}
                                        values={{
                                            suffix: vm.formData.getNotifyDeliveryTeam() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                        }}
                                        className='replays-toggle-description'
                                    />}
                                    popoverText={intl.formatMessage(NOTIFICATION_ENABLED_INFO_MESSAGE_TEXT)}
                                    className='meeting-toggle'
                                />
                            </div>
                            <div className='scheduled-email-form__image-container'>
                                <UploadImageS3Presentation
                                    vm={formData.uploadEmailImageS3VM}
                                    fieldName={intl.formatMessage(LOGO_IMAGE_TEXT)}
                                />
                            </div>
                            <EmailVariablesSection type={EMAIL_LIST_TYPE.RESPONSIVE}/>
                            <FormField
                                className="m-top-5px scheduled-email-form__name-field"
                                label={intl.formatMessage(EMAIL_NAME_INTERNAL_TEXT)}
                                labelFor="scheduled-label"
                            >
                                <div className="flex">
                                    <input
                                        id="scheduled-label"
                                        className="oe-form-field__control"
                                        value={formData.name}
                                        onChange={e => formData.setName(e.target.value)}
                                        type="text"
                                    />
                                </div>
                                {formData.formChecked && formData.getNameError() && (
                                    <span className="oe-form-field__error">
                                        {formData.getNameError()}
                                    </span>
                                )}
                            </FormField>
                            <div className='scheduled-email-form__date-and-time'>
                                <div className='scheduled-email-form__date-and-time-fields'>
                                    <FormField className="scheduled-email-form__form-field">
                                        <div className="scheduled-email-form__label">
                                            <label className="oe-form-field__title">
                                                {intl.formatMessage(DATE_TEXT)}
                                            </label>
                                        </div>
                                        <DefaultDatePicker
                                            className="oe-form-field__control"
                                            dateformat={vm.getDateFormat()}
                                            showTimeSelect={false}
                                            selected={formData.date}
                                            onChange={date => formData.setDate(date)}
                                            highlightDates={vm.getEventDatesArray()}
                                        />
                                    </FormField>
                                    <FormField
                                        label={intl.formatMessage(MEETING_TIME_TEXT)}
                                        className="scheduled-email-form__form-field scheduled-email-form__form-column"
                                    >
                                        <div className='scheduled-email-form__time'>
                                            <TimeInput
                                                className="oe-form-field__control"
                                                date={formData.dateTime}
                                                onChange={date => formData.setTime(date)}
                                                onError={mobx.action(() => formData.timeValidationError = true)}
                                            />
                                            <div className='scheduled-email-form__timezone'>
                                                {formattedTimezoneOffset}
                                            </div>
                                        </div>
                                    </FormField>
                                </div>
                                {formData.formChecked && !!formData.getDateError() &&
                                    <span className="oe-form-field__error">
                                        {formData.getDateError()}
                                    </span>
                                }
                            </div>
                            <div className="scheduled-email-form__content">
                                <FormField
                                    className="m-top-5px"
                                    label={intl.formatMessage(EMAIL_SUBJECT_TEXT)}
                                    labelFor="email-subject"
                                >
                                    <div className="flex">
                                        <input
                                            id="email-subject"
                                            className="oe-form-field__control"
                                            value={
                                                formData.emailSubject
                                            }
                                            onChange={e =>
                                                formData.setEmailSubject(e.target.value)
                                            }
                                            type="text"
                                        />
                                    </div>
                                    {formData.formChecked && formData.isEmailSubjectError() && (
                                        <span className="oe-form-field__error">
                                            {formData.emailSubjectError}
                                        </span>
                                    )}
                                </FormField>
                                <FormField
                                    label={intl.formatMessage(EMAIL_BODY_TEXT)}
                                    labelFor="email-body"
                                    className="with-editor"
                                >
                                    <RichTextEditor
                                        className="m-top-0px body-editor"
                                        id="email-body"
                                        value={formData.emailBody}
                                        onChange={mobx.action((_, editor) => {
                                            formData.setEmailBody(editor.getData());
                                        })}
                                        validationError={formData.formChecked && formData.getEmailBodyError()}
                                    />
                                </FormField>
                            </div>
                        </div>
                    </div>
                    </>
                }
                footer={
                    <>
                        {vm.isSaving &&
                            <span className='oe-popup-layout__footer-status-area'>
                                {intl.formatMessage(EMAIL_IS_BEING_SAVED_TEXT)}
                            </span>
                        }
                        <div className="buttons-block buttons-block--from-right">
                            <DefaultCloseSubWindowButton
                                text={intl.formatMessage(vm.isEdit ? APPLY_AND_CLOSE_TEXT : ADD_TEXT)}
                                modifier={['fat', 'confirm-safe']}
                                onClick={vm.performSaveCall}
                                disabled={vm.isSaving || formData.isImageUploading()}
                            />
                            {vm.isEdit && <DefaultButton
                                text={intl.formatMessage(APPLY_TEXT)}
                                modifier={['fat', 'confirm-safe']}
                                onClick={vm.performSaveCall}
                                disabled={vm.isSaving || formData.isImageUploading()}
                            />}
                            <DefaultCloseSubWindowButton
                                text={intl.formatMessage(CANCEL_TEXT)}
                            />
                        </div>
                    </>
                }
            />
        );
    })
);

export { ScheduledEmailFormPresentation };
